/**
 * components/riskTerminal/phone/phoneModel.js
 *
 * upstox-076 — the pure parts of the phone layout: names, dates, the stop mark, the detail sheet's
 * fields, the held-quantity index for the watchlist chain, the day-limit bar. Every value is the
 * panel's own (model.js does the figures); nothing here is computed from anything the desktop does
 * not already show.
 */

import { legShort, qtyText, priceText, avgText, rowCharges, mcxNote } from '../model';
import { hhmm } from '../theme';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-13" → "13 Oct" */
export function dayMon(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? Number(m[3]) + ' ' + MON[Number(m[2]) - 1] : '';
}

/** One expiry → "13 Oct"; legs on different expiries → "Oct/Dec" (months), or "13 Oct/20 Oct" inside one month. */
export function expiryText(legs) {
  const ds = [...new Set((legs || []).map(l => l.expiry).filter(Boolean))].sort();
  if (!ds.length) return '';
  if (ds.length === 1) return dayMon(ds[0]);
  const months = [...new Set(ds.map(d => MON[Number(d.slice(5, 7)) - 1]))];
  return months.length > 1 ? months.join('/') : ds.map(dayMon).join('/');
}

/** The expiry shared by every leg, or '' when they differ (the group line only says a shared one). */
export function sharedExpiry(legs) {
  const ds = [...new Set((legs || []).map(l => l.expiry).filter(Boolean))];
  return ds.length === 1 ? dayMon(ds[0]) : '';
}

const stripYear = sym => {
  const m = String(sym || '').match(/^(.* \d{2} [A-Z]{3}) \d{2}$/);
  return m ? m[1] : sym || '';
};

/** "22300 CE" — strike and type; the expiry only when the group's legs differ. A future keeps its name. */
export function legName(l, symbolOf, withExpiry) {
  const sym = l.trading_symbol || symbolOf(l.instrument_key);
  const short = legShort(sym);
  const base = short === sym ? stripYear(sym) : short;
  return withExpiry && l.expiry && short !== sym ? base + ' · ' + dayMon(l.expiry) : base;
}

export const isHedge = l => String(l.opening_intent || '').toUpperCase() === 'HEDGE';

/**
 * The mark after a leg's name: 'failed' (stop failed — the server's `stop_failed`), 'hedge' (a hedge
 * leg needs no stop), or null. The desktop has no armed-stop field yet, so an "armed" ring is never
 * drawn: a mark only where data drives it.
 */
export function stopMark(l) {
  if (l.stop_failed) return 'failed';
  if (isHedge(l)) return 'hedge';
  return null;
}

/** The instrument for the sheet's title: "NIFTY 22200 PE · 13 Oct" (the leg's own fields), else the symbol. */
export function instrumentTitle(l, symbolOf) {
  if (l.underlying && l.strike != null && l.option_type) {
    return l.underlying + ' ' + Number(l.strike) + ' ' + l.option_type + (l.expiry ? ' · ' + dayMon(l.expiry) : '');
  }
  return stripYear(l.trading_symbol || symbolOf(l.instrument_key));
}

/**
 * The sheet's grid: QTY, AVG, LTP, INTENT, STOP, CHARGES, OPENED, EXPIRY, LOT — from the leg. A value the
 * server does not have is "—" with the reason (`tip`), as on the desktop.
 */
export function sheetFields(l, symbolOf, positionsReason) {
  const ch = rowCharges(l.charges, [{ sym: l.trading_symbol || symbolOf(l.instrument_key), c: l.charges }]);
  const unknown = l.pnl == null ? (l.pnl_reason || positionsReason || 'no broker row for it') : '';
  const mark = stopMark(l);
  return [
    { k: 'QTY', v: qtyText(l.quantity), tip: l.quantity == null ? unknown : '' },
    { k: 'AVG', v: avgText(l.average_price), tip: l.average_price == null ? unknown || 'breakeven not known' : 'the breakeven' },
    { k: 'LTP', v: priceText(l.last_price), tip: l.last_price == null ? unknown : '' },
    { k: 'INTENT', v: l.opening_intent ? String(l.opening_intent).toUpperCase() : '—', tip: l.opening_intent ? "the opening order's declared intent" : 'no declared intent on the opening order' },
    { k: 'STOP', v: mark === 'failed' ? '■ failed' : mark === 'hedge' ? '— hedge' : 'not tracked', tip: mark ? '' : 'stop not tracked — placed outside the terminal' },
    { k: 'CHARGES', v: ch.text, tip: ch.title, dotted: ch.dotted },
    { k: 'OPENED', v: l.first_seen_at ? hhmm(l.first_seen_at) || '—' : '—', tip: l.first_seen_at ? '' : 'the terminal did not see it open' },
    { k: 'EXPIRY', v: l.expiry ? dayMon(l.expiry) : '—', tip: '' },
    { k: 'LOT', v: l.lot_size != null ? String(l.lot_size) : '—', tip: '' },
  ];
}

/** The banner on a leg whose stop failed: only what the server says (time and the broker's words when sent). */
export function stopBanner(l) {
  if (!l.stop_failed) return null;
  return 'Stop failed to place' + (l.stop_failed_at ? ' at ' + (hhmm(l.stop_failed_at) || l.stop_failed_at) : '')
    + (l.stop_failed_message ? ' · broker said: ' + l.stop_failed_message : '');
}

// ── the watchlist chain's held-quantity chips ──

const heldKey = (u, exp, strike, type) => [u, exp || '', Number(strike), type].join('|');

/** net quantity per contract, over every leg this account holds (today's groups, holdings, unclassified) */
export function heldIndex(panel) {
  const idx = {};
  const add = l => {
    if (!l || l.quantity == null || !l.underlying || l.strike == null || !l.option_type) return;
    const k = heldKey(l.underlying, l.expiry, l.strike, l.option_type);
    idx[k] = (idx[k] || 0) + l.quantity;
  };
  ((panel && panel.structures && panel.structures.items) || []).forEach(s => (s.legs || []).forEach(add));
  ((panel && panel.holdings && panel.holdings.items) || []).forEach(s => (s.legs || []).forEach(add));
  ((panel && panel.unclassified && panel.unclassified.items) || []).forEach(add);
  return idx;
}

/** "+65" / "−130" where this account holds the contract of that chain row, else null */
export function heldChip(idx, list, strike, side) {
  const u = list && list.rule && list.rule.underlying;
  if (!u) return null;
  const q = idx[heldKey(u, list.expiry, strike, side)];
  return q ? qtyText(q) : null;
}

// ── the day band's limit bar ──

/** { label, fillPct, amberPct, redPct, tone } — null when there is no bar to draw. */
export function limitBar(M, money) {
  if (!M.computed || M.noLimit || M.limit == null) return null;
  const m = money || {};
  const used = Math.max(0, Math.min(M.used || 0, 100));
  let label;
  if (M.zone === 'OVER') label = 'OVER BY ₹' + Math.round(m.over_by || 0).toLocaleString('en-IN');
  else label = '₹' + Math.round(m.left_amount != null ? m.left_amount : 0).toLocaleString('en-IN') + ' LEFT · ' + Math.round(M.used || 0) + '%';
  return { label, fillPct: M.zone === 'OVER' ? 100 : used, amberPct: M.amberPct, redPct: M.redPct, tone: M.zone === 'OVER' || M.zone === 'RED' ? 'red' : M.zone === 'AMBER' ? 'amber' : 'grey' };
}

/** "₹30k" for the bar's caption */
export function shortRupees(n) {
  if (n == null) return '';
  const a = Math.abs(n);
  if (a >= 1e5 && a % 1e5 === 0) return '₹' + a / 1e5 + 'L';
  if (a >= 1000 && a % 1000 === 0) return '₹' + a / 1000 + 'k';
  return '₹' + Math.round(a).toLocaleString('en-IN');
}

/** "CONSOLIDATING" → "CONSOL." so a cell reads on 360 px */
export const shortWords = w => String(w || '').replace('CONSOLIDATING', 'CONSOL.');

/** An order's status word for the completed list */
export function doneStatus(o) {
  const grp = o.status_group || (String(o.order_status || '').toLowerCase() === 'rejected' ? 'REJECTED' : 'FILLED');
  const filled = o.filled_quantity || 0;
  if (grp === 'REJECTED') return { text: 'rejected', red: true };
  if (grp === 'CANCELLED') return { text: filled > 0 ? 'part ' + filled + '/' + o.quantity + ' · cancelled' : 'cancelled', red: false };
  if (grp === 'PARTIAL') return { text: 'part ' + filled + '/' + o.quantity, red: false };
  return { text: 'filled', red: false };
}

export { mcxNote };
